import EditOutlined from '@ant-design/icons/EditOutlined';
import Descriptions from 'antd/es/descriptions';
import Divider from 'antd/es/divider';
import Empty from 'antd/es/empty';
import Tag from 'antd/es/tag';
import Typography from 'antd/es/typography';
import React, { useMemo } from 'react';
import styled from 'styled-components';

import { type Resource } from '@mai/types';

import { SectionTitle } from './TaskDescription';
import { EditButton, TitleWrapper } from './TaskHeader';

const { Text } = Typography;

const SidebarSection = styled.div`
  margin-bottom: 16px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`;

const MetadataContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MetadataItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MetadataKey = styled(Text)`
  font-size: 12px;
`;

const MetadataValue = styled(Text)`
  font-weight: 500;
`;

const SidebarInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// Helper function to format date
const formatDate = (input: string | Date): string => {
  const date = input instanceof Date ? input : new Date(input);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });
};

interface MetadataItem {
  key: string;
  value: string;
}

interface TaskSidebarProps {
  status: string | null | undefined;
  createdAt: Date | null | undefined;
  updatedAt: Date | null | undefined;
  metadata: Record<string, string>;
  onEditMetadata: () => void;
  resources: Resource[];
}

const TaskSidebar: React.FC<TaskSidebarProps> = ({
  status,
  createdAt,
  updatedAt,
  metadata,
  onEditMetadata,
}) => {
  const statusText = useMemo(() => {
    if (status === 'running') {
      return 'Running';
    } else if (status === 'completed') {
      return 'Completed';
    } else if (status === 'failed') {
      return 'Failed';
    } else if (status === 'cancelled') {
      return 'Cancelled';
    } else {
      return 'Pending';
    }
  }, [status]);

  const metadataKeyValues = Object.entries(metadata);

  return (
    <SidebarInner>
      <SidebarSection>
        <SectionTitle level={4}>Details</SectionTitle>
        <Descriptions size="small" column={1}>
          <Descriptions.Item label="Status">
            <Tag>{statusText}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Created">
            {createdAt ? formatDate(createdAt.toISOString()) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Last Updated">
            {updatedAt ? formatDate(updatedAt.toISOString()) : '-'}
          </Descriptions.Item>
        </Descriptions>
      </SidebarSection>

      <Divider style={{ margin: '0' }} />

      <SidebarSection>
        <SectionHeader>
          <TitleWrapper>
            <SectionTitle level={4} style={{ margin: 0 }}>
              Metadata
            </SectionTitle>
            <EditButton
              type="text"
              icon={<EditOutlined />}
              size="small"
              aria-label="Edit metadata"
              onClick={onEditMetadata}
            />
          </TitleWrapper>
        </SectionHeader>
        {metadataKeyValues.length > 0 ? (
          <MetadataContainer>
            {metadataKeyValues.map(([key, value], index) => (
              <MetadataItem key={index}>
                <MetadataKey>{key}</MetadataKey>
                <MetadataValue>{value}</MetadataValue>
              </MetadataItem>
            ))}
          </MetadataContainer>
        ) : (
          <Empty description="No metadata available." />
        )}
      </SidebarSection>
    </SidebarInner>
  );
};

export default TaskSidebar;
